import * as React from 'react';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Modal, Select, Input, Alert, DatePicker, InputNumber, message, Row } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { connect } from 'react-redux';
import moment from 'moment';
import { imageUploadValidation, normFile } from '../../utils/ImageUtils';
import { getProtectedImageUriForSupplier } from '../../utils/FileAccessUtils';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import TextAreaWithAudio from '../audio_note_recorder/TextAreaWithAudio';
import {
	compareStrings,
	getDisabledDate,
	getDisabledDateTime,
	getImageCountErrorRules,
	getObjectValues,
	getServiceCallConfigValueBasedOnSupplierOrInternalTeam,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { WORK_ORDER_STATUSES } from '../../constants/WorkOrderStatuses';
import {
	CONTRACTOR_CHECK_OUT_OPTIONS,
	INTERNAL_TECH_CHECK_OUT_OPTIONS,
	COMPLETED_CHECKOUT_VALUES,
	PARTS_REQUESTED_CHECK_OUT_OPTION,
	PARTS_ON_ORDER_CHECK_OUT_OPTION,
} from '../../constants/ServiceCallCheckOutOptions';
import { retrieveCachedUserDetails } from '../../thunks/session_thunks';
import { resolutionTypesRestCrudThunksForSupplier } from '../../thunks/resolution_types_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import OWUpload from '../OWUpload';
import { taskItemsRestCrudThunksForSupplier } from '../../thunks/task_items_thunks';
import { isAValidAndParitalWorkOrder, isWOTaskCheckFailed } from '../../utils/WorkOrderUtils';
import { isThirdPartySupplier } from '../../utils/AuthUtils';
import { wallTimeToUtcIso } from '../../utils/DataFormatterUtils';
import { DateTimeWithTimeZone } from '../date_time_with_time_zone/DateTimeWithTimeZone';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	canLogRefrigerantEvents,
	getRefrigerantTrackedAppliances,
	getWorkOrderAssetIds,
	isRefrigerantEventRequiredForCheckOut,
	REFRIGERANT_CHECK_OUT_APPLIANCES_TC,
	REFRIGERANT_CHECK_OUT_EVENTS_TC,
} from '../../utils/RefrigerantUtils';
import {
	refrigerantEventsRestCrudThunksForSupplier,
	retrieveRefrigerantAssetSummary,
} from '../../thunks/refrigerant_events_thunks';
import { assetsRestCrudThunksForSupplier } from '../../thunks/assets_thunks';
import CheckOutRefrigerantSection from '../refrigerant/CheckOutRefrigerantSection';

const FormItem = Form.Item;
export const DATE_FORMAT = 'MMMM D, YYYY h:mm a';

/**
 * Every control in this vertical form runs the full width of the modal body.
 *
 * The fields had drifted to five different widths — the status Select and the Notes box capped at
 * 300, Number of Techs at 100, Resolution Type at 800 (past the 600px modal, so full width by
 * accident), and only the date pickers actually at 100% — which reads as a ragged right edge down
 * the form.
 *
 * An INLINE style rather than a `w-full` utility, and the reason is the same cascade trap the
 * refrigerant panel's margin hit: antd's own component CSS (`.ant-input-number { width: 90px }`, and
 * friends) is injected by babel-plugin-import AFTER tailwind.out.css, so at equal specificity the
 * later rule wins and the utility is a silent no-op. An inline style outranks both stylesheets
 * whatever order they land in. `DateTimeWithTimeZone` already sizes its own controls this way.
 */
const FULL_WIDTH_CONTROL: React.CSSProperties = { width: '100%' };

/**
 * Whether this check-out is even in the refrigerant gate's population.
 *
 * The permission half is not cosmetic: gating a check-out on a write the server refuses is a work
 * order nobody can close, so a third party is judged against this work order.
 *
 * An EDIT of an already-checked-out service call (`formData.checkOutTime` is set) is deliberately OUT.
 * The gate exists to stop a work order being completed on an enrolled appliance without a ledger row;
 * once it has been completed, refusing the edit prevents nothing and instead makes every work order
 * closed BEFORE this feature shipped impossible to correct. The nudge for those lives where it belongs
 * — the work order's own Refrigerant Tracking tab and its Log Event action.
 *
 * A plain function rather than a method, because the constructor reads it while seeding
 * `refrigerantFetching` and a class field would have it depend on field-initialisation order.
 */
const shouldCheckRefrigerant = (props) =>
	// `.detail`, not the slice root: `companyConfig` here is the whole `company_config` slice — every
	// other read in this file goes through `detail.config....`, and `AccessPermissionChecker` maps
	// `state.company_config.detail` for exactly this call. Handing over the slice would silently miss
	// the org's configuration and fall back to the permission's default.
	canLogRefrigerantEvents(
		nullSafeGet('currentUser', props),
		nullSafeGet('companyConfig.detail', props),
		nullSafeGet('workOrder', props)
	) &&
	!!nullSafeGet('workOrder.id', props) &&
	!nullSafeGet('formData.checkOutTime', props) &&
	getWorkOrderAssetIds(nullSafeGet('workOrder', props)).length > 0;

interface ManualCheckOutModalFormProps extends FormComponentProps {
	onSubmit: any;
	onCancel: any;
	currentUser: any;
	companyConfig: any;
	createErrors: any;
	updateErrors: any;
	visible: boolean;
	creating: boolean;
	updating: boolean;

	formData: any;
	privateSupplier: boolean;
	resolutionTypes: any;
	fetchResolutionTypes: any;
	fetchMultipleResolutionTypes: any;
	workOrder: any;
	fetchTaskItems: any;
	fetchRefrigerantAppliances: any;
	fetchRefrigerantEvents: any;
	fetchRefrigerantSummary: any;
}

class ManualCheckOutModalForm extends React.Component<ManualCheckOutModalFormProps, any> {
	constructor(props) {
		super(props);
		this.state = {
			previewVisible: false,
			uploadLoading: false,
			previewImage: false,
			photoInfo: [],
			allPhotos: [],
			taskItems: [],
			isAudioBusy: false,
			refrigerantAppliances: [],
			refrigerantEvents: [],
			refrigerantSummaries: {},
			refrigerantFailed: false,
			// Seeded TRUE whenever the gate could possibly apply, and the validator treats an
			// unresolved check as blocking — the same shape as `taskItemsFetching` above it. The
			// alternative reads "no tracked appliance" for as long as the two requests are in flight,
			// which is a mandatory gate that a fast tech walks straight through.
			refrigerantFetching: shouldCheckRefrigerant(props),
		};
	}

	componentDidMount() {
		const { fetchTaskItems, workOrder, form } = this.props;

		if (!nullSafeGet('id', workOrder)) return;
		this.resolveRefrigerantRequirement();
		this.setState({ taskItemsFetching: true });
		fetchTaskItems({ workOrderId: workOrder.id }, 'checkoutModalFormIndex', {
			current: 1,
			pageSize: 500,
		})
			.then((taskItems) =>
				this.setState(
					{
						taskItems,
						taskItemsFetching: false,
					},
					() => {
						form.validateFields(['checkOutStatus'], { force: true });
					}
				)
			)
			.catch(message.error)
			.finally(() => this.setState({ taskItemsFetching: false }));
	}

	/**
	 * Everything the panel and the log drawer need, in one round trip: which of this work order's
	 * assets are enrolled, whether the ledger already references the work order, and each candidate
	 * appliance's derived compliance summary.
	 *
	 * The appliances are resolved BY ID rather than read off `workOrder.asset` because this modal is
	 * opened from two surfaces carrying two different work-order shapes — see `getWorkOrderAssetIds`.
	 * A third party on the hydrated shape reads them off the work order instead: the by-id read drops
	 * assets it cannot read. The ledger read is filtered on `workOrderId`, so an event logged against
	 * the same appliance on a different job does not satisfy this one.
	 *
	 * The SUMMARIES are what make the drawer's Refrigerant field arrive prefilled. Refrigerant is
	 * configured in two places — on the asset type, describing the equipment class, and on the unit —
	 * and a unit that takes its class's answer has `refrigerantTypeCode` null on its own row, which is
	 * all `/asset/assets/multiple/:ids` returns. The summary is the resolved unit-over-class answer
	 * (`RefrigerantLeakRateUtil.effectiveRefrigerantFields`), which is why the drawer reads it as its
	 * second source. It also powers the drawer's live leak-rate warning, so a tech logging from
	 * check-out now sees the same projection they get from the appliance's own tab.
	 *
	 * Fetched for every CANDIDATE id here rather than in a second hop keyed on the enrolled ones,
	 * because the drawer's seed goes in through antd's `initialValues` — read once when the Form
	 * mounts. A summary that lands after the tech has clicked Log Refrigerant Event is a summary that
	 * never reaches the field, so it has to be in hand before the panel offers the button. The ids are
	 * known up front and there is normally exactly one.
	 *
	 * A failure of either gate read leaves the gate OPEN and says so in the panel: we would be blocking
	 * on evidence we do not have, and the cost of the other direction is a work order that cannot be
	 * closed for as long as the request keeps failing. A failed SUMMARY is caught per appliance and
	 * costs only the prefill — it must never be able to fail the gate reads beside it. No
	 * `message.error` for any of it — the panel is the place to say it, and a toast on top of a modal
	 * the tech is mid-way through filling in is noise.
	 */
	resolveRefrigerantRequirement = () => {
		const {
			workOrder,
			currentUser,
			form,
			fetchRefrigerantAppliances,
			fetchRefrigerantEvents,
			fetchRefrigerantSummary,
		} = this.props;

		if (!shouldCheckRefrigerant(this.props)) return;

		const assetIds = getWorkOrderAssetIds(workOrder);
		const isThirdParty = isThirdPartySupplier(currentUser);
		// Drop inactive and deleted units as the by-id read does; the server refuses a deleted one.
		const appliancesRequest =
			isThirdParty && !isAValidAndParitalWorkOrder(workOrder)
				? Promise.resolve(
						getRefrigerantTrackedAppliances(workOrder).filter(
							(asset) => asset.isActive !== false && !asset.isDeleted
						)
				  )
				: fetchRefrigerantAppliances(assetIds);
		const summaryWorkOrderId = isThirdParty ? workOrder.id : undefined;

		Promise.all([
			appliancesRequest,
			fetchRefrigerantEvents(workOrder.id),
			Promise.all(
				assetIds.map((assetId) =>
					fetchRefrigerantSummary(assetId, summaryWorkOrderId).catch(() => null)
				)
			),
		])
			.then(([assets, events, summaries]: any) =>
				this.setState(
					{
						refrigerantAppliances: (assets || []).filter((asset) =>
							nullSafeGet('isRefrigerantTracked', asset)
						),
						refrigerantEvents: events || [],
						// Keyed on the summary's OWN `assetId` rather than zipped by index: the per-appliance
						// catch above turns a failure into a `null` hole, and an index-based pairing would
						// then hand the next appliance's numbers to the wrong unit.
						refrigerantSummaries: (summaries || []).filter(Boolean).reduce(
							(byAssetId, summary) => ({
								...byAssetId,
								[nullSafeGet('assetId', summary)]: summary,
							}),
							{}
						),
						refrigerantFailed: false,
						// Cleared HERE rather than in a `finally`, because `isRefrigerantGateBlocking` counts
						// the flag as blocking: forcing validation while it is still true makes the rule
						// reject a completing status regardless of what the reads just returned, and a
						// `finally` that clears the flag afterwards does not revalidate — leaving "Log a
						// refrigerant event" under the status field on a work order that owes nothing, until
						// the tech changes the status or submits.
						refrigerantFetching: false,
					},
					() => form.validateFields(['checkOutStatus'], { force: true })
				)
			)
			// Revalidated on the way out for the same reason the success path is: the tech may have picked
			// a completing status while the reads were still in flight, and the rule rejected on
			// `refrigerantFetching` alone. Failing OPEN clears the requirement, but antd will not re-run
			// the rule off a state change — without this the fail-open warning shows in the panel while
			// the field below still demands an event nobody can be asked for.
			.catch(() =>
				this.setState({ refrigerantFailed: true, refrigerantFetching: false }, () =>
					form.validateFields(['checkOutStatus'], { force: true })
				)
			);
	};

	/**
	 * Whether the refrigerant gate refuses THIS status right now — the single derivation behind both
	 * the field error and the panel's severity, so the two cannot disagree.
	 *
	 * Two ways to be blocking, and the `COMPLETED_CHECKOUT_VALUES` test is what separates them:
	 *
	 *  - **Still resolving.** Until the appliance and ledger reads land we do not know whether an event
	 *    is owed, and the safe answer is "not yet" — the same stance `taskItemsFetching` takes. But only
	 *    for a status that COMPLETES the work order. Parts on Order, Quote Required, Delayed by Third
	 *    Party and Work Incomplete leave the job open and can never owe a ledger row, so a slow or
	 *    hanging read must not be able to stop a tech checking out under one of them.
	 *  - **Resolved and owed.** `isRefrigerantEventRequiredForCheckOut` tests the completing status
	 *    itself, so it already carries that half of the condition.
	 *
	 * A FAILED read is not blocking either way — `refrigerantFailed` leaves the appliance list empty and
	 * the panel says so; see the fail-open note on `resolveRefrigerantRequirement`.
	 */
	isRefrigerantGateBlocking = (checkOutStatus) =>
		(this.state.refrigerantFetching && COMPLETED_CHECKOUT_VALUES.includes(checkOutStatus)) ||
		isRefrigerantEventRequiredForCheckOut({
			checkOutStatus,
			trackedApplianceCount: this.state.refrigerantAppliances.length,
			loggedEventCount: this.state.refrigerantEvents.length,
		});

	/**
	 * A row the tech just logged from inside this modal, folded into the same list the validator
	 * reads. Appended rather than re-fetched: the ledger is append-only, the drawer resolves with the
	 * created row, and a re-read would put a network round trip between the tech's Continue and the
	 * error clearing.
	 */
	handleRefrigerantEventLogged = (event) => {
		const { form } = this.props;
		this.setState(
			(state) => ({ refrigerantEvents: [...state.refrigerantEvents, event] }),
			() => form.validateFields(['checkOutStatus'], { force: true })
		);
	};

	handlePreview = (obj) => {
		let thumbUrl = null;
		let fullUrl = null;
		let photoInfo = null;
		if (obj.response.data) {
			const imgFileId = obj.response.data.fileId;
			const imgFileName = obj.response.data.fileName;
			thumbUrl = getProtectedImageUriForSupplier(imgFileId, imgFileName, 100, 100);
			fullUrl = getProtectedImageUriForSupplier(imgFileId, imgFileName, 400, 400);
			photoInfo = {
				uid: imgFileId,
				size: 1,
				name: imgFileName,
				filename: imgFileName,
				url: fullUrl,
				status: 'done',
				thumbUrl: thumbUrl,
				response: { data: { fileName: imgFileName, fileId: imgFileId } },
				type: 'image/jpeg',
			};
		}

		this.setState({
			photoInfo: [photoInfo],
			previewImage: fullUrl || thumbUrl,
			previewVisible: true,
		});
	};

	handleImageUpdate = (info) => {
		if (info.file.status === 'done') {
			this.setState({ allPhotos: info.fileList });
		} else if (info.file.status === undefined) {
			info.fileList = this.state.allPhotos;
		}
	};

	handleRemove = (file) => {
		const allPhotos = nullSafeGetOrElse('state.allPhotos', this, []);
		this.setState({
			allPhotos: allPhotos.filter((_) => _.uid !== file.uid),
		});
	};

	handleCancel = () => this.setState({ previewVisible: false });

	render() {
		const {
			visible,
			currentUser,
			onCancel,
			onSubmit,
			form,
			formData,
			createErrors,
			updateErrors,
			privateSupplier,
			companyConfig,
			creating,
			updating,
			resolutionTypes,
			fetchResolutionTypes,
			fetchMultipleResolutionTypes,
			workOrder,
		} = this.props;
		const { previewVisible, previewImage } = this.state;
		const { getFieldDecorator } = form;
		const okText = 'Check Out';
		const titleText = 'Remote Check Out';
		const resolutionsFetching = nullSafeGet('fetching', resolutionTypes);
		const hasResolutionTypes = resolutionsFetching
			? true
			: getObjectValues(nullSafeGetOrElse('records', resolutionTypes, {})).length > 0;
		const photoStrings = this.state.allPhotos.map((p) => {
			const fileName = nullSafeGetOrElse('response.data.fileName', p, '');
			const fileId = nullSafeGetOrElse('response.data.fileId', p, '');
			return `${fileId}/${fileName}`;
		});

		const BACKEND_URI = getBackendUri();
		const uploadHeaders = { 'X-Auth-Token': retrieveCachedUserDetails(['token']).token };
		getFieldDecorator(`id`, { initialValue: formData.id });
		getFieldDecorator(`checkOutByEmail`, { initialValue: currentUser.email });
		getFieldDecorator(`checkOutImages`, { initialValue: photoStrings });
		const userType = nullSafeGet('userType', currentUser);

		const checkOutNotesMandatory = getServiceCallConfigValueBasedOnSupplierOrInternalTeam(
			workOrder,
			currentUser,
			'checkOutNotesMandatory',
			false
		);
		const numImagesRequired = getServiceCallConfigValueBasedOnSupplierOrInternalTeam(
			workOrder,
			currentUser,
			'numImagesRequiredOnCheckOut',
			0
		);
		const imageCountErrorMessage = `You need to upload at least ${numImagesRequired} photo${
			numImagesRequired > 1 ? 's' : ''
		}`;

		const ADDITIONAL_CHECK_OUT_OPTIONS = nullSafeGetOrElse(
			'detail.config.partsOrderingConfig.needPartsRequisitionBeforeOrdering',
			companyConfig,
			false
		)
			? PARTS_REQUESTED_CHECK_OUT_OPTION
			: PARTS_ON_ORDER_CHECK_OUT_OPTION;

		const partsRequestedStatus = compareStrings(
			workOrder.status,
			WORK_ORDER_STATUSES.partsRequested
		);

		const checkOutOptions = privateSupplier ? (
			<Select style={FULL_WIDTH_CONTROL}>
				{[
					...(partsRequestedStatus ? [] : INTERNAL_TECH_CHECK_OUT_OPTIONS),
					...ADDITIONAL_CHECK_OUT_OPTIONS,
				].map((d) => (
					<Select.Option key={d.value} value={d.value}>
						{d.displayName}
					</Select.Option>
				))}
			</Select>
		) : (
			<Select style={FULL_WIDTH_CONTROL}>
				{[
					...(partsRequestedStatus ? [] : CONTRACTOR_CHECK_OUT_OPTIONS),
					...ADDITIONAL_CHECK_OUT_OPTIONS,
				].map((d) => (
					<Select.Option key={d.value} value={d.value}>
						{d.displayName}
					</Select.Option>
				))}
			</Select>
		);

		const tasksCheckFailed =
			this.state.taskItemsFetching || isWOTaskCheckFailed(workOrder, this.state.taskItems);
		// Read off the currently selected status, so the panel's severity tracks the same answer the
		// field's own rule gives — see `isRefrigerantGateBlocking`.
		const refrigerantEventRequired = this.isRefrigerantGateBlocking(
			form.getFieldValue('checkOutStatus')
		);
		const { checkInTime } = formData;
		const checkOutTimeZone =
			form.getFieldValue('checkOutTimeZone') || nullSafeGet('location.timeZoneId', workOrder);

		return (
			<Modal
				visible={visible}
				width={600}
				title={titleText}
				okText={okText}
				onCancel={onCancel}
				onOk={onSubmit}
				closable={false}
				confirmLoading={creating || updating}
				okButtonProps={{
					disabled: this.state.isAudioBusy,
				}}
			>
				<Form layout="vertical" className="supplierForm">
					{createErrors.length > 0 ? (
						<FormItem>
							<Alert message={createErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					{updateErrors.length > 0 ? (
						<FormItem>
							<Alert message={updateErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					{/*
					  Above the status field, not below it: the panel is the reason the status field can
					  refuse, and a tech who reads the refusal first has to hunt for what to do about it.
					  Rendered only when the gate applies at all — see `shouldCheckRefrigerant`.
					*/}
					{shouldCheckRefrigerant(this.props) ? (
						<CheckOutRefrigerantSection
							workOrder={workOrder}
							appliances={this.state.refrigerantAppliances}
							events={this.state.refrigerantEvents}
							summaries={this.state.refrigerantSummaries}
							loading={this.state.refrigerantFetching}
							failed={this.state.refrigerantFailed}
							required={refrigerantEventRequired}
							onLogged={this.handleRefrigerantEventLogged}
						/>
					) : null}
					<FormItem label="Check Out Status">
						{getFieldDecorator('checkOutStatus', {
							rules: [
								{
									required: true,
									message: 'Please select a status',
								},
								{
									message: 'All tasks need to be completed before checkout!',
									validator: function (_, value) {
										if (tasksCheckFailed && COMPLETED_CHECKOUT_VALUES.includes(value)) {
											return Promise.reject(
												new Error('All tasks need to be completed before checkout!')
											);
										}
										return Promise.resolve();
									}.bind(this),
								},
								{
									message: 'Log a refrigerant event before completing check out.',
									/*
									  Attached to `checkOutStatus` rather than to a field of its own, because the
									  requirement is a property of the STATUS being chosen: completing the work order
									  on an enrolled appliance is what owes the ledger a row, while checking out as
									  Parts on Order does not. `value` is passed rather than the derived flag re-read,
									  so the rule judges the status antd is actually validating.
									*/
									validator: (_, value) =>
										this.isRefrigerantGateBlocking(value)
											? Promise.reject(
													new Error('Log a refrigerant event before completing check out.')
											  )
											: Promise.resolve(),
								},
							],
							initialValue: partsRequestedStatus
								? ADDITIONAL_CHECK_OUT_OPTIONS[0].value
								: formData.checkOutStatus || privateSupplier
								? INTERNAL_TECH_CHECK_OUT_OPTIONS[0].value
								: CONTRACTOR_CHECK_OUT_OPTIONS[0].value,
						})(checkOutOptions)}
					</FormItem>
					{(!COMPLETED_CHECKOUT_VALUES.includes(
						form.getFieldsValue(['checkOutStatus']).checkOutStatus
					) && (
						<FormItem label="Estimated completion date ">
							{getFieldDecorator(`estimatedCompletionDate`, {
								initialValue: formData.estimatedCompletionDate
									? moment(formData.estimatedCompletionDate)
									: undefined,
							})(
								<DatePicker style={FULL_WIDTH_CONTROL} showTime={false} format={'MMMM D, YYYY'} />
							)}
						</FormItem>
					)) ||
						null}
					<FormItem label="Number of Techs">
						{getFieldDecorator('numberOfTechs', {
							rules: [
								{
									required: true,
									message: 'Please enter the number of techs involved',
								},
							],
							initialValue: 1 + nullSafeGetOrElse('additionalTechnicianEmails.length', formData, 0),
						})(<InputNumber style={FULL_WIDTH_CONTROL} placeholder="techs" />)}
					</FormItem>
					<DateTimeWithTimeZone
						form={form}
						location={nullSafeGet('location', workOrder)}
						displayName={'Check Out Time'}
						timeZoneName={'checkOutTimeZone'}
						dateName={'checkOutTime'}
						initialValue={formData.checkOutTime}
						rules={[
							{
								type: 'object',
								required: true,
								message: 'Please select the time technician(s) finished work.',
							},
							{
								message: 'Check-out time must be after check-in time.',
								validator: (_, value) => {
									if (!value) return Promise.resolve();

									const checkOutUtcIso = wallTimeToUtcIso(value, checkOutTimeZone);
									const checkOutUtc = moment(checkOutUtcIso).utc();

									const checkInUtc = moment(checkInTime).utc().subtract(1, 'minute');

									if (checkOutUtc.isBefore(checkInUtc)) {
										return Promise.reject(new Error('Check-out time must be after check-in time!'));
									}
									return Promise.resolve();
								},
							},
							{
								message: 'Check-out time must be before the current time.',
								validator: (_, value) => {
									if (!value) return Promise.resolve();

									const checkOutUtcIso = wallTimeToUtcIso(value, checkOutTimeZone);
									const checkOutUtc = moment(checkOutUtcIso).utc();

									const nowUtc = moment().utc().add(15, 'minute');

									if (checkOutUtc.isAfter(nowUtc)) {
										return Promise.reject(
											new Error('Check-out time must be before the current time!')
										);
									}
									return Promise.resolve();
								},
							},
						]}
						disabledDate={getDisabledDate(checkInTime, checkOutTimeZone, moment())}
						disabledTime={getDisabledDateTime(
							moment(checkInTime).subtract(1, 'minute'),
							checkOutTimeZone,
							moment().add(1, 'minute')
						)}
					/>
					<FormItem label="Add After Photos">
						{getFieldDecorator(`images`, {
							valuePropName: 'fileList',
							getValueFromEvent: normFile,
							initialValue: this.state.allPhotos,
							rules: getImageCountErrorRules(numImagesRequired, imageCountErrorMessage),
						})(
							<OWUpload
								accept="image/*"
								name="file"
								listType="picture-card"
								className="servicecall-image-uploader"
								multiple={true}
								headers={uploadHeaders}
								onPreview={this.handlePreview}
								action={`${BACKEND_URI}/api/v1/supplier/file/upload`}
								beforeUpload={imageUploadValidation}
								onChange={this.handleImageUpdate}
								onRemove={this.handleRemove}
							>
								<div style={{ display: 'inline' }}>
									<LegacyIcon type={this.state.uploadLoading ? 'loading' : 'plus'} />
									<div className="ant-upload-text">Upload</div>
								</div>
								<Modal visible={previewVisible} footer={null} onCancel={this.handleCancel}>
									<img alt="example" style={{ width: '100%' }} src={previewImage} />
								</Modal>
							</OWUpload>
						)}
					</FormItem>
					<FormItem label="Notes">
						{getFieldDecorator('checkOutNotes', {
							initialValue: {
								text: formData.checkOutNotes,
								audioUrl: formData.checkOutAudio,
							},
							rules: [
								{
									validator: (_, val, callback) => {
										if (checkOutNotesMandatory && !(val && val.text) && !(val && val.audioUrl)) {
											callback('Please add notes or a voice note');
										} else {
											callback();
										}
									},
								},
							],
						})(
							<TextAreaWithAudio
								style={FULL_WIDTH_CONTROL}
								userType={userType}
								onBusyStateChange={(busy) => this.setState({ isAudioBusy: busy })}
							/>
						)}
					</FormItem>
					{hasResolutionTypes ? (
						<Form.Item label="Resolution Type">
							{getFieldDecorator('resolutionTypeId', {
								initialValue: formData.resolutionTypeId,
							})(
								<OWAsyncSelect
									style={FULL_WIDTH_CONTROL}
									stateSlice={resolutionTypes}
									targetCollectionName={'RESOLUTION_TYPES_AUTOCOMPLETE'}
									fetchMultiple={(ids, targetCollectionName) => {
										fetchMultipleResolutionTypes(ids, targetCollectionName);
									}}
									fetchData={(
										searchText,
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									) => {
										fetchResolutionTypes(
											{ name: searchText },
											targetCollectionName,
											pagination,
											sorting,
											filters,
											addToTargetCollection
										);
									}}
									renderRecord={(resolutionType) => (
										<Select.Option key={resolutionType.id} value={resolutionType.id}>
											{resolutionType.resolutionCode ? `${resolutionType.resolutionCode} - ` : null}
											{resolutionType.resolutionType}
										</Select.Option>
									)}
									sortBy={{ sort_by: 'name', order: 'ascend' }}
									allowClear
								/>
							)}
						</Form.Item>
					) : null}
				</Form>
			</Modal>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	visible: ownProps.visible,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config,
	createErrors: state.buyer_contacts.createErrors,
	updateErrors: state.buyer_contacts.updateErrors,
	creating: state.work_orders.creating,
	updating: state.work_orders.updating,

	privateSupplier: ownProps.privateSupplier,
	resolutionTypes: state.resolution_types,
});

const mapDispatchToProps = (dispatch) => ({
	fetchMultipleResolutionTypes: (ids, targetCollectionName) =>
		dispatch(resolutionTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchResolutionTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			resolutionTypesRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchTaskItems: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			taskItemsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	/*
	  The supplier thunks unconditionally: this modal exists only on the supplier side of the app —
	  buyers do not check techs out.
	*/
	fetchRefrigerantAppliances: (assetIds) =>
		dispatch(
			assetsRestCrudThunksForSupplier.readMultiple(assetIds, REFRIGERANT_CHECK_OUT_APPLIANCES_TC)
		),
	fetchRefrigerantEvents: (workOrderId) =>
		dispatch(
			refrigerantEventsRestCrudThunksForSupplier.read(
				{ workOrderId },
				REFRIGERANT_CHECK_OUT_EVENTS_TC,
				// The whole ledger for one work order. A single visit's events are a handful, and the
				// gate counts them — a page boundary would make "no events" mean "none on page one".
				{ current: 1, pageSize: 100 }
			)
		),
	// Deliberately NOT in the redux store — the thunk keeps it out, because it is a derived snapshot
	// tied to one `asOf` and caching it would show a stale leak rate right after an event changed it.
	fetchRefrigerantSummary: (assetId, workOrderId) =>
		dispatch(retrieveRefrigerantAssetSummary(ROLE_TYPES.SUPPLIER)(assetId, workOrderId)),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(Form.create<ManualCheckOutModalFormProps>()(ManualCheckOutModalForm));
